import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { FiEdit3 } from "react-icons/fi";
import { FaMapMarker } from "react-icons/fa";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import PlanStaffingHoverMenu from "components/Plans/PlanStaffingHoverMenu";

import { PLAN_GRID_VIEWS } from "components/Plans";
import StaffingDayHeader from "components/Plans/staffing/StaffingDayHeader";
import NeedNameCell from "components/Plans/staffing/NeedNameCell";
import { getEmployeesTooltipDetails } from "components/Plans/staffing/staffingTooltip";
import { useStaffingTableInteractions } from "components/Plans/staffing/useStaffingTableInteractions";

const getStaffingLevelRows = (
	props,
	selectedDay,
	hoveredCellId,
	tooltipCellId,
	handleCellMouseEnter,
	handleCellMouseLeave
) => {
	return props.staffing_level_assignments.map((assignment, i) => {
		const counterForSelectedDay = assignment.counters.find((counter) =>
			moment(counter.day).isSame(selectedDay, "day")
		);
		const needed = counterForSelectedDay
			? counterForSelectedDay.hasOwnProperty("overwrite")
				? counterForSelectedDay.overwrite
				: counterForSelectedDay.needed
			: 0;
		const isHidden = selectedDay && needed === 0;

		return (
			<tr key={i} className={isHidden ? "hidden" : ""}>
				<NeedNameCell assignment={assignment} />

				{getStaffingLevelRowContent(
					props,
					assignment,
					i,
					selectedDay,
					hoveredCellId,
					tooltipCellId,
					handleCellMouseEnter,
					handleCellMouseLeave
				)}
			</tr>
		);
	});
};

const getStaffingLevelRowContent = (
	props,
	assignment,
	row,
	selectedDay,
	hoveredCellId,
	tooltipCellId,
	handleCellMouseEnter,
	handleCellMouseLeave
) =>
	assignment.counters.map((item, i) => {
		const checked = item.checked || false;
		const details = getEmployeesTooltipDetails(item.employees);
		const needed = item.hasOwnProperty("overwrite")
			? item.overwrite
			: item.needed;
		const tooltip = (
			<Tooltip className="tooltip-staffing">
				{item.current}/{needed}
				{details}
			</Tooltip>
		);

		item.function = assignment.function;
		item.shift = assignment.shift;

		if (assignment.ubication) item.ubication = assignment.ubication;

		var classBackground = "";
		if (needed < item.current) {
			classBackground = "bg-green";
		}

		if (needed > item.current) {
			classBackground = "bg-red";
		}

		// Ocultar días no seleccionados cuando hay un día seleccionado
		if (selectedDay && !moment(item.day).isSame(selectedDay, "day")) {
			return <td key={i} className="need-cell"></td>;
		}

		const cellKey = `${row}-${i}`;
		const isHovered = hoveredCellId === cellKey;
		const showTooltip = tooltipCellId === cellKey;

		return (
			<OverlayTrigger
				key={i}
				placement="top"
				overlay={tooltip}
				show={showTooltip}
				trigger={[]}
			>
				<td
					key={i}
					className={`need-cell btn-checkbox ${checked ? "active" : ""}`}
					data-cell-key={cellKey}
					onMouseEnter={() => handleCellMouseEnter(cellKey)}
					onMouseLeave={(e) => handleCellMouseLeave(e, cellKey)}
					style={{ position: "relative" }}
				>
					<div className="react-contextmenu-wrapper">
						<input
							type="checkbox"
							id={`need_${row}_${i}`}
							onChange={(e) => props.handleNeedSelection(row, i, false, e)}
							checked={checked}
						/>
						<label
							htmlFor={`need_${row}_${i}`}
							className="btn-check-label"
							draggable
							onDragEnter={() => props.handleNeedSelection(row, i, true)}
						></label>

						{!(needed === 0 && item.current === 0) && (
							<div className={`need-value-box ${classBackground}`}>
								{needed}
								{item.overwrite && item.overwrite !== item.needed ? (
									<FiEdit3 className="need-icon icon-edited" />
								) : (
									""
								)}
							</div>
						)}
					</div>

					{/* Renderizar el menú hover cuando está hovereado */}
					{isHovered && (
						<PlanStaffingHoverMenu
							itemStaffing={item}
							row={row}
							index={i}
							cellKey={cellKey}
							onMouseEnter={() => handleCellMouseEnter(cellKey)}
							onMouseLeave={(e) => handleCellMouseLeave(e, cellKey)}
							onBringEmployees={
								props.handleBringEmployeesTemporarily
									? (itemStaffing, rowIndex, index) => {
											const overrides = {};
											if (!itemStaffing.checked) {
												overrides.replace = true;
											}
											props.handleNeedSelection(
												rowIndex,
												index,
												false,
												{ target: { checked: true } },
												overrides
											);
											props.handleBringEmployeesTemporarily(itemStaffing);
									  }
									: undefined
							}
							onEdit={(itemStaffing, row, index) => {
								// Primero seleccionar la celda para actualizar needs_selection.
								// replace: true solo si la celda no estaba ya seleccionada,
								// para no perder una selección múltiple de la misma fila.
								const overrides = {};
								if (!itemStaffing.checked) {
									overrides.replace = true;
								}
								props.handleNeedSelection(
									row,
									index,
									false,
									{ target: { checked: true } },
									overrides
								);
								// Luego abrir el modal con los datos actualizados
								setTimeout(
									() => props.handleEditStaffingLevel(itemStaffing),
									50
								);
							}}
						/>
					)}
				</td>
			</OverlayTrigger>
		);
	});

const getStaffingLevelCurrentRows = (props, selectedDay) => {
	return props.staffing_level_current_assignments.map((assignment, i) => {
		const fun = assignment.function;
		const shift = assignment.shift;
		if (!shift) return null;

		var text_ubication = "";

		if (assignment.ubication) {
			const ubication = assignment.ubication;
			var color;

			if (ubication.border_color) color = ubication.border_color;

			if (ubication.background_color) color = ubication.background_color;

			text_ubication = (
				<span className="need-ubication">
					<FaMapMarker
						className="icon"
						style={{
							color: color && color,
						}}
					/>{" "}
					{assignment.ubication.name}
				</span>
			);
		}

		// Añadir clase "hidden" si `current` es 0 para el día seleccionado
		const counterForSelectedDay = assignment.counters.find((counter) =>
			moment(counter.day).isSame(selectedDay, "day")
		);
		const isHidden =
			selectedDay &&
			(!counterForSelectedDay || counterForSelectedDay.current === 0);

		const need_name = fun.name + " en " + shift.name;
		return (
			<tr key={i} className={isHidden ? "hidden" : ""}>
				<th>
					<div className="th-wrap">
						<div title={need_name} className="need-name substring">
							{need_name} {text_ubication}
						</div>
					</div>
				</th>

				{getStaffingLevelCurrentRowContent(assignment, selectedDay)}
			</tr>
		);
	});
};

const getStaffingLevelCurrentRowContent = (assignment, selectedDay) =>
	assignment.counters.map((item, i) => {
		const details = getEmployeesTooltipDetails(item.employees);
		const tooltip = (
			<Tooltip className="tooltip-staffing">
				{item.current}
				{details}
			</Tooltip>
		);

		// Ocultar días no seleccionados cuando hay un día seleccionado
		if (selectedDay && !moment(item.day).isSame(selectedDay, "day")) {
			return <td key={i} className="current-cell"></td>;
		}

		return (
			<OverlayTrigger key={i} placement="top" overlay={tooltip}>
				<td key={i} className="current-cell">
					{item.current}
				</td>
			</OverlayTrigger>
		);
	});

const PlanStaffingLevels = (props) => {
	const { plan_grid_view } = props;
	const { t } = useTranslation();

	const {
		selectedDay,
		hoveredCellId,
		tooltipCellId,
		hoveredHeaderDay,
		handleDayClick,
		handleHeaderMouseEnter,
		handleHeaderMouseLeave,
		handleCellMouseEnter,
		handleCellMouseLeave,
	} = useStaffingTableInteractions();

	const handleHeaderAddClick = (itemStaffingHeader) => {
		props.openNewStaffingLevelModal(itemStaffingHeader);
		handleHeaderMouseLeave({});
	};

	var tableClass = "";
	if (plan_grid_view === PLAN_GRID_VIEWS.ubications)
		tableClass = "table-ubications";

	if (plan_grid_view === PLAN_GRID_VIEWS.default) tableClass = "table-default";

	if (plan_grid_view === PLAN_GRID_VIEWS.grid) tableClass = "table-grid";

	if (
		props.staffing_level_assignments.length > 0 ||
		props.staffing_level_current_assignments.length > 0
	) {
		return (
			<>
				<table
					id="tableNeeds"
					className={`table table-bordered table-plan table-needs ${tableClass}`}
				>
					<thead>
						<tr>
							<th className="main-col z-30">
								<div className="th-wrap">{t("staffingLevels.needs")}</div>
							</th>
							<StaffingDayHeader
								planDays={props.plan_days}
								planGridView={plan_grid_view}
								selectedDay={selectedDay}
								hoveredHeaderDay={hoveredHeaderDay}
								onDayClick={handleDayClick}
								onHeaderMouseEnter={handleHeaderMouseEnter}
								onHeaderMouseLeave={handleHeaderMouseLeave}
								onHeaderAddClick={handleHeaderAddClick}
								t={t}
							/>
						</tr>
					</thead>
					<tbody>
						{props.staffing_level_assignments.length > 0
							? getStaffingLevelRows(
									props,
									selectedDay,
									hoveredCellId,
									tooltipCellId,
									handleCellMouseEnter,
									handleCellMouseLeave
							  )
							: getStaffingLevelCurrentRows(props, selectedDay)}
					</tbody>
				</table>
			</>
		);
	} else return "";
};

export default PlanStaffingLevels;
